import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../../shared/components';

@Component({
  standalone: false,
  selector: 'app-item-postings-material-set-dialog',
  templateUrl: './item-postings-material-set-dialog.component.html',
  styleUrls: ['./item-postings-material-set-dialog.component.scss'],
})
export class ItemPostingsMaterialSetDialogComponent implements OnInit, OnDestroy {
  materialSetOptions: any[] = [];
  materialSets: any[] = [];
  selectedMaterialSet: any = null;
  materialForms: { [materialId: string]: FormGroup } = {};
  expandedMaterialId: string = null;
  lineItemQuantity: number = 0;
  bidItemName = '';
  selectedMaterialSetForm: FormGroup;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    public dialogRef: MatDialogRef<ItemPostingsMaterialSetDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
  ) {
    this.materialSets = data?.materialSets || [];
    this.lineItemQuantity = data?.lineItemQuantity || 0;
    this.bidItemName = data?.bidItemName || '';

    this.materialSetOptions = this.materialSets.map(ms => ({
      value: ms.id,
      label: ms.name,
    }));

    this.selectedMaterialSetForm = new FormGroup({
      materialSetId: new FormControl(data?.selectedMaterialSetId || null),
    });

    // Restore existing materials if editing
    if (data?.selectedMaterialSetId) {
      this.onMaterialSetChange(data.selectedMaterialSetId, data?.materials || []);
    }
  }

  ngOnInit() {
    // Listen to dropdown changes
    this.selectedMaterialSetForm.controls['materialSetId'].valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(value => {
        if (value) {
          this.onMaterialSetChange(value);
        }
      });

    // Auto-select if only 1 material set and none selected
    if (this.materialSets.length === 1 && !this.selectedMaterialSetForm.controls['materialSetId'].value) {
      this.selectedMaterialSetForm.controls['materialSetId'].setValue(this.materialSets[0].id);
    }
  }

  private onMaterialSetChange(materialSetId: string, existingMaterials?: any[]) {
    this.selectedMaterialSet = this.materialSets.find(ms => ms.id === materialSetId) || null;
    this.materialForms = {};
    this.expandedMaterialId = null;

    if (!this.selectedMaterialSet) return;

    const existingMap = {};
    (existingMaterials || []).forEach(m => {
      existingMap[m.material_id] = m.quantity;
    });

    this.selectedMaterialSet.bid_item_material_set_material?.forEach((msm, index) => {
      const materialId = msm.material?.id;
      const existingQty = existingMap[materialId];
      const calculatedQty = existingQty != null
        ? existingQty
        : (this.lineItemQuantity || 0) * (parseFloat(msm.conversion_factor) || 0);

      const form = new FormGroup({
        quantity: new FormControl(calculatedQty || null),
      });

      // Update top quantity when install qty changes
      form.controls['quantity'].valueChanges
        .pipe(takeUntil(this.onDestroy))
        .subscribe(() => this.updateTotalQuantity());

      this.materialForms[materialId] = form;

      // Auto-expand first material
      if (index === 0) {
        this.expandedMaterialId = materialId;
      }
    });

    this.updateTotalQuantity();
  }

  private updateTotalQuantity() {
    this.lineItemQuantity = Object.values(this.materialForms).reduce((sum, form) => {
      return sum + (parseFloat(form.controls['quantity']?.value) || 0);
    }, 0);
  }

  toggleMaterial(materialId: string) {
    this.expandedMaterialId = this.expandedMaterialId === materialId ? null : materialId;
  }


  done() {
    if (!this.selectedMaterialSet) {
      this.dialogRef.close(null);
      return;
    }

    const materials = this.selectedMaterialSet.bid_item_material_set_material?.map(msm => {
      const materialId = msm.material?.id;
      const form = this.materialForms[materialId];
      return {
        quantity: parseFloat(form?.controls['quantity']?.value) || 0,
        bid_item_material_set: {
          id: this.selectedMaterialSet.id,
          name: this.selectedMaterialSet.name,
        },
        bid_item_material_set_id: this.selectedMaterialSet.id,
        bid_item_material_set_material: {
          id: msm.id,
          conversion_factor: msm.conversion_factor,
        },
        bid_item_material_set_material_id: msm.id,
        material: msm.material,
        material_id: materialId,
      };
    }) || [];

    this.dialogRef.close({
      materialSetId: this.selectedMaterialSet.id,
      materialSetName: this.selectedMaterialSet.name,
      materials,
    });
  }

  deleteMaterialSet() {
    const materialSetName = this.selectedMaterialSet?.name || 'this material set';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Warning',
        `The material set ${materialSetName} will be removed if you proceed. Do you want to continue?`,
        'Continue',
        'Cancel',
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dialogRef.close({ deleted: true });
      }
    });
  }

  close() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
